import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { api } from "@/lib/api"

// ───────────────────────────────────────────────────────────────────────
// Backend contracts (matched to legacy admin's POSTs):
//
//   POST /admin/get_sub_price       (no body)
//                                   → { success, data: [{ months, price, ... }], message }
//                                   data is an ARRAY indexed by `months`:
//                                     - months: 1  → monthly price
//                                     - months: 12 → yearly price
//
//   POST /admin/update_sub_price    { subscription_price_monthly, subscription_price_yearly }
//                                   → { success, message }
//                                   Both numbers. Legacy sent the InputNumber
//                                   raw value (could be number or string '0').
// ───────────────────────────────────────────────────────────────────────

type SubPriceRow = {
  months: number
  price: number
  _id?: string
  created_at?: string
  updated_at?: string
}

type SubPrices = {
  monthly: number
  yearly: number
  /** Raw rows in case other periods get added in the future. */
  raw: SubPriceRow[]
}

/**
 * Fetches current monthly + yearly subscription pricing. Defaults to 0 if
 * a period is missing in the response (matching legacy's initial state).
 */
export function useSubPrices() {
  return useQuery({
    queryKey: ["admin", "subscriptions", "prices"] as const,
    queryFn: async (): Promise<SubPrices> => {
      const res = await api.post<{
        success: boolean
        data: SubPriceRow[]
        message: string
      }>("/admin/get_sub_price", {})
      const rows = Array.isArray(res.data?.data) ? res.data.data : []
      const find = (m: number) => rows.find((r) => r.months === m)?.price ?? 0
      return {
        monthly: find(1),
        yearly: find(12),
        raw: rows,
      }
    },
    staleTime: 60_000,
  })
}

type UpdateSubPricePayload = {
  subscription_price_monthly: number
  subscription_price_yearly: number
}

export function useUpdateSubPrices() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: UpdateSubPricePayload) => {
      const res = await api.post<{ success: boolean; message: string }>(
        "/admin/update_sub_price",
        payload
      )
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "subscriptions"] })
    },
  })
}
